import { useRef } from "react";

import { AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";

import { ToolCallContentWindow } from "src/agents/AgentVersionPage/AgentPlayground";
import { FloatingChart } from "src/analytics/FloatingChart/FloatingChart";
import {
  FloatingWindowsContext,
  FloatingWindowsContextType,
} from "src/base-components/FloatingWindow/context";
import { useFloatingWindows } from "src/base-components/FloatingWindow/hooks";
import {
  createFloatingWindowsStore,
  FloatingWindowProps,
  FloatingWindowType,
} from "src/base-components/FloatingWindow/store";
import { BulkCaseActionWindow } from "src/cases/BulkCaseActionWindow";
import { CaseActionFormWindow } from "src/cases/caseView/components/CaseActionFormWindow";
import { FileViewerFloatingWindow } from "src/cases/caseView/components/FileViewerFloatingWindow";
import { HistoryFormViewWindow } from "src/cases/caseView/components/HistoryFormViewWindow";
import { CaseCopilotWindow } from "src/cases/copilot/CaseCopilotWindow";
import { CopilotWindow } from "src/copilot/CopilotWindow";
import { FeatureQueryCopilotWindow } from "src/copilot/FeatureQueryCopilotWindow";
import { DetailedView } from "src/dataTable/DetailedView/DetailedView";
import { FlowGraphSearchWindow } from "src/flowGraph/FlowGraphSearchWindow";
import { RawJsonSchemaEditorWindow } from "src/jsonSchema/editor/RawJsonSchemaEditorWindow";
import { FlowJsonSchemaWindow } from "src/schema/window/FlowJsonSchemaWindow";
import { ManifestNodeBuilderWindow } from "src/schema/window/ManifestNodeBuilderWindow";

export const FloatingWindowsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const storeRef = useRef<FloatingWindowsContextType>();

  if (!storeRef.current) {
    storeRef.current = createFloatingWindowsStore();
  }

  return (
    <FloatingWindowsContext.Provider value={storeRef.current}>
      {children}
      <FloatingWindows />
    </FloatingWindowsContext.Provider>
  );
};

const FloatingWindowComponents: Record<
  FloatingWindowType,
  React.FC<FloatingWindowProps>
> = {
  "detailed-view": DetailedView,
  chart: FloatingChart,
  copilot: CopilotWindow,
  "feature-query-copilot": FeatureQueryCopilotWindow,
  "json-schema-editor": RawJsonSchemaEditorWindow,
  "flow-json-schema": FlowJsonSchemaWindow,
  "cm-action": CaseActionFormWindow,
  "cm-bulk-action": BulkCaseActionWindow,
  "cm-history": HistoryFormViewWindow,
  "cm-file": FileViewerFloatingWindow,
  "manifest-node-builder": ManifestNodeBuilderWindow,
  "flow-graph-search": FlowGraphSearchWindow,
  "tool-call-content": ToolCallContentWindow,
  "case-copilot": CaseCopilotWindow,
} as const;

const FloatingWindows: React.FC = () => {
  const windows = useFloatingWindows();

  return createPortal(
    <div className="fixed top-0 left-0 isolate z-40">
      <AnimatePresence initial={false}>
        {Object.entries(windows).map(([id, window]) => {
          const Component = FloatingWindowComponents[window.type];

          return (
            <Component
              key={id}
              computePosition={window.computePosition}
              data={window.data}
              id={id}
            />
          );
        })}
      </AnimatePresence>
    </div>,
    document.body,
  );
};
